import { ReactNode, useState } from 'react';

import { AnimatePresence } from 'framer-motion';
import styled from 'styled-components';

import { Rolling } from '@components/animators/rolling';
import { Slide } from '@components/animators/slide';
import { Button } from '@components/button';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconArrowLeft } from '@components/icons/arrow-left';
import { IconElement } from '@components/icons/element';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import { ElementsList } from './elements-list';
import { TreeView } from './tree-view';

const locale = {
  en: {
    pageElements: 'Page elements',
    addElement: 'Add element',
    buttonAdd: 'Add',
    form: 'Form',
  },
};

export const FieldSidebarLabel = ({ label }: { label: string }) => {
  const trimmedLabel = label.trim();
  return (
    <Text
      font="regular14"
      color={trimmedLabel ? 'textPrimary' : 'textHelper'}
      text={trimmedLabel || 'No label'}
    />
  );
};

export const FieldsSidebar = ({ extraFieldGroups }: { extraFieldGroups?: ReactNode }) => {
  const [sideView, setSideView] = useState<'form' | 'elements'>('elements');
  const showForm = sideView === 'form';
  const { t } = useScopedTranslate(locale);

  const SidebarIcon = showForm ? IconElement : IconAddPlus;

  return (
    <LeftSidebar id="left-sidebar">
      <TopContainer>
        <TitleStack orientation="horizontal" space={2} align="center">
          <SidebarIcon color="elementBlue100" />
          <Rolling variable={showForm} style={{ minWidth: 0 }}>
            <Text
              font="bold18"
              color="textPrimary"
              ellipsisWithTooltip
              text={showForm ? t('pageElements') : t('addElement')}
            />
          </Rolling>
        </TitleStack>

        <ToggleButton>
          {showForm ? (
            <Button
              variant="secondary"
              text={t('buttonAdd')}
              iconLeft={<IconAddPlus />}
              onClick={() => setSideView('elements')}
            />
          ) : (
            <Button
              variant="secondary"
              iconLeft={<IconArrowLeft />}
              text={t('form')}
              onClick={() => setSideView('form')}
            />
          )}
        </ToggleButton>
      </TopContainer>
      <Separator />
      <ElementsContainer>
        <AnimatePresence initial={false}>
          {showForm ? (
            <Slide key="elements" offset={30}>
              <TreeView />
            </Slide>
          ) : (
            <ElementsList extraFieldGroups={extraFieldGroups} />
          )}
        </AnimatePresence>
      </ElementsContainer>
    </LeftSidebar>
  );
};

const ElementsContainer = styled.div({
  padding: pxToRem(24),
  paddingBottom: pxToRem(40),
  flex: 1,
  minHeight: 0, // lets this flex child scroll instead of stretching the sidebar
  overflow: 'hidden auto',
});

const TopContainer = styled.div({
  padding: `${pxToRem(16)} ${pxToRem(24)}`,
  display: 'flex',
  alignItems: 'center',
  gap: pxToRem(16),
  flexShrink: 0,
});

const TitleStack = styled(Stack)({
  flex: 1,
  minWidth: 0,
});

const ToggleButton = styled.div({
  flexShrink: 0,
});

const LeftSidebar = styled.div({
  background: theme.color.backgroundLightDark,
  boxShadow: `${pxToRem(-1)} 0px 0px 0px ${theme.color.border10} inset`,
  // Fixed header + scrollable body, so the Add element / Form toggle stays pinned.
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
});
